import { useEffect } from 'react';
import PropTypes from 'prop-types';
import Wordmark from '../../../UISystem/Wordmark';
import ConnectionAvatar from '../../../UISystem/ConnectionAvatar';
import { ButtonRounded } from '../../../UISystem/Buttons';
import { COLORS } from '../../../../styles/UISystem';
import { firstNameOf } from '../../../../utils/contactUtils';
import { logFunnelInterstitialShown } from '../../../../services/analytics/eventLogger';

/**
 * AnonTextInterstitial — the TEXT variant's scan landing (variants block
 * plan 2, epic §3/§8): wordmark, host photo, "Connect with {firstName}"
 * (back per device pass 1k — trying it against "Hi let's connect"), black
 * [{firstName}'s info]. No subtitle (user rulings, device pass 1j — the
 * button names the payload; the headline names the intent; done).
 *
 * Pure view-gate — formation stays mount-fired in AnonExchangeFlow like every
 * shipped iteration (true tap-gated formation is a rails change, not bought
 * here). A no-tap is a clean "no interest" signal: the mount logs the
 * interstitial micro-event, making shown-count the honest denominator.
 *
 * ConnectionAvatar owns the no-photo case (initials/placeholder circle) — a
 * photo-less host renders a clean slot, never a broken image.
 */
const AnonTextInterstitial = ({ chatRoomId, profileData, onConnect }) => {
  const firstName = firstNameOf(profileData?.name);

  useEffect(() => {
    if (chatRoomId) logFunnelInterstitialShown(chatRoomId);
  }, [chatRoomId]);

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        backgroundColor: COLORS.WHITE,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      <Wordmark style={{ marginTop: 'calc(env(safe-area-inset-top) + 48px)' }} />

      {/* Host identity + the format/privacy promise — centered */}
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <ConnectionAvatar photoUrl={profileData?.photoUrl} name={profileData?.name} size={96} />
        <div
          style={{
            fontSize: '26px',
            fontWeight: '700',
            color: '#000000',
            marginTop: '24px',
            padding: '0 24px',
            textAlign: 'center',
          }}
        >
          {firstName ? `Connect with ${firstName}` : 'Connect'}
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          paddingBottom: 'calc(48px + env(safe-area-inset-bottom))',
        }}
      >
        {/* "{name}'s info" (user edit 2026-08-13, was "See {name}'s info") —
            the door names the destination; "Connect" lives on the share
            sheet's submit, where the connection actually happens */}
        <ButtonRounded
          text={firstName ? `${firstName}'s info` : 'See info'}
          onClick={onConnect}
          variant="primary"
          size="auto"
          style={{ minWidth: '170px' }}
        />
      </div>
    </div>
  );
};

AnonTextInterstitial.propTypes = {
  chatRoomId: PropTypes.string,
  profileData: PropTypes.shape({
    name: PropTypes.string,
    photoUrl: PropTypes.string,
  }),
  onConnect: PropTypes.func.isRequired,
};

export default AnonTextInterstitial;
